import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay'
import { HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core'
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { ServiceWorkerModule } from '@angular/service-worker'
import { ProgressBarComponent } from '@components/progress-bar/progress-bar.component'
import { LoaderFullFragment } from '@fragments/loader-full/loader-full.fragment'
import { MEGA_AUTH_LOGO_PATH } from '@megaphone/auth'
import {
  API_PARAMETERS,
  BASE_URL,
  CHANNEL_TYPE,
  COOKIES,
  DEFAULT_AUTH_SETTINGS,
  ENDPOINT,
  ITEM_ID,
  ORGANISATION,
  ORGANISATION_TYPE,
  PRIVATE_MEDIA,
  SESSION,
  SnackbarBaseService,
} from '@megaphone/bases'
import { DarkModeService } from '@megaphone/dark-mode'
import { MegaHttpInterceptorModule } from '@megaphone/http-interceptor'
import { LanguageServiceModule } from '@megaphone/language-service'
import { CAN_EDIT, MegaOrganisationService, MegaSessionService } from '@megaphone/services'
import { MegaSplashScreenService } from '@megaphone/splash-screen'
import { MegaphoneStatusModule } from '@megaphone/status'
import { TranslateLoader, TranslateModule } from '@ngx-translate/core'
import { CloudSnackbarService } from '@services/cloud-snackbar.service'
import { CalendarModule, DateAdapter } from 'angular-calendar'
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns'
import { AppComponent } from 'app/app.component'
import { environment } from 'environments/environment'
import { NgxBackButtonModule, NgxBackButtonService } from 'ngx-back-button'
import { NgxHrefModule } from 'ngx-href'
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader'
import { BehaviorSubject } from 'rxjs'
import { AppRoutingModule } from './app-routing.module'

export function HttpLoaderFactory(_httpBackend: HttpBackend) {
  return new MultiTranslateHttpLoader(_httpBackend, ['/assets/i18n/', '/assets/vendors/i18n/'])
}

export function initSession(sessionService: MegaSessionService) {
  return () => sessionService.initSession()
}

export function initDarkMode(darkModeService: DarkModeService) {
  return () => darkModeService.init()
}

export function initSplashScreen(megaSplashScreenService: MegaSplashScreenService) {
  return () => megaSplashScreenService.init(true)
}

export function initOrganisation(megaOrganisationService: MegaOrganisationService) {
  if (!localStorage.getItem('token')) return () => false

  return async () => await megaOrganisationService.init()
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    // Components
    ProgressBarComponent,
    // Shared
    LoaderFullFragment,
    // Mat
    MatSnackBarModule,
    // config
    MegaphoneStatusModule.forRoot({
      version: environment.version,
    }),
    // Interceptors
    MegaHttpInterceptorModule.forRoot({
      environment: environment,
    }),
    // Language
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    LanguageServiceModule.forRoot({
      languages: [{ id: 'de' }, { id: 'fr' }, { id: 'it' }, { id: 'en' }],
      skipLocationUpdate: true,
    }),
    // Vendors
    NgxBackButtonModule.forRoot({}),
    NgxHrefModule.forRoot({
      defaultTargetAttr: '_blank',
    }),
    CalendarModule.forRoot({ provide: DateAdapter, useFactory: adapterFactory }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    { provide: ITEM_ID, useValue: new BehaviorSubject(null) },
    {
      provide: COOKIES,
      useValue: new BehaviorSubject({
        necessary: true,
        useful: {
          googleMap: true,
        },
      }),
    },
    {
      provide: API_PARAMETERS,
      useValue: {},
    },
    { provide: BASE_URL, useValue: `${environment.baseUrl}/${environment.baseVersion}` },
    { provide: ENDPOINT, useValue: '' },
    { provide: SESSION, useValue: new BehaviorSubject(null) },
    { provide: ORGANISATION, useValue: new BehaviorSubject(null) },
    { provide: ORGANISATION_TYPE, useValue: new BehaviorSubject(null) },
    { provide: CHANNEL_TYPE, useValue: null },
    { provide: SnackbarBaseService, useClass: CloudSnackbarService },
    { provide: CAN_EDIT, useValue: true },
    { provide: PRIVATE_MEDIA, useValue: false },
    { provide: DEFAULT_AUTH_SETTINGS, useValue: { google: true } },
    { provide: MEGA_AUTH_LOGO_PATH, useValue: '' },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => null,
      deps: [NgxBackButtonService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initOrganisation,
      deps: [MegaOrganisationService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initSession,
      deps: [MegaSessionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initDarkMode,
      deps: [DarkModeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initSplashScreen,
      deps: [MegaSplashScreenService],
      multi: true,
    },
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-symbols-rounded' } },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 0,
        touchendHideDelay: 1500,
        position: 'above',
        disableTooltipInteractivity: true,
      },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
